import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import type Configuration from "../types/Configuration";
import { scrubUrl } from "../utils/launch-params";

interface ErrorMetadata {
  [key: string]: any;
}

interface TelemetryUser {
  id?: string | null;
  name?: string | null;
  email?: string | null;
}

// The user cannot be set before start(), so hold it until a client exists.
let pendingUser: TelemetryUser | null = null;

const applyUser = (user: TelemetryUser): void => {
  Bugsnag.setUser(
    user.id ?? undefined,
    user.email ?? undefined,
    user.name ?? undefined
  );
};

const start = (
  apiKey: string,
  releaseStage: string,
  stackName: string
): void => {
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    releaseStage,
    metadata: {
      customer: {
        stackName,
      },
    },
    // Bugsnag records the page URL itself, which on the launch routes still
    // carries the launch/code/session_id credentials.
    onError: (event) => {
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      return true;
    },
  });
  if (pendingUser) {
    applyUser(pendingUser);
    pendingUser = null;
  }
};

// Start at bootstrap using the build-time key. bugsnagInit below cannot report
// pre-backend failures, since the key it uses arrives in that response. No-ops
// without a build-time key, leaving bugsnagInit as the fallback.
const bugsnagBootstrap = (releaseStage: string, stackName: string): void => {
  if (Bugsnag.isStarted()) return;
  const apiKey = process.env.REACT_APP_BUGSNAG_API_KEY;
  if (!apiKey) return;
  start(apiKey, releaseStage, stackName);
};

/** Enrich Bugsnag from the server configuration, or start it if bootstrap didn't. */
const bugsnagInit = (configuration: Configuration[]): void => {
  const appSettings = configuration?.[0]?.app_settings;
  const urlParams = new URLSearchParams(window.location.search);
  const user: TelemetryUser = {
    id: urlParams.get("emr_user_id"),
    name: urlParams.get("emr_user_full_name"),
    email: urlParams.get("emr_user_email"),
  };
  const customer_name = window.location.host.split(".")[0];

  if (Bugsnag.isStarted()) {
    // Running from bootstrap. Bugsnag's own releaseStage is fixed at start(),
    // so the server's value is recorded as metadata to cross-check against.
    applyUser(user);
    if (appSettings?.release_stage) {
      Bugsnag.addMetadata(
        "customer",
        "serverReleaseStage",
        appSettings.release_stage
      );
    }
    return;
  }

  if (appSettings?.bugsnag_api_key && appSettings?.release_stage) {
    pendingUser = user;
    start(
      appSettings.bugsnag_api_key,
      appSettings.release_stage,
      customer_name
    );
  }
};

/** Set the reporting identity. Buffered until Bugsnag has started. */
const setTelemetryUser = (user: TelemetryUser): void => {
  if (!Bugsnag.isStarted()) {
    pendingUser = user;
    return;
  }
  applyUser(user);
};

const logError = (
  error: Error,
  errorMetadata: ErrorMetadata | null = null
): void => {
  if (Bugsnag.isStarted()) {
    Bugsnag.notify(error, (event) => {
      event.addMetadata("custom", errorMetadata);
    });
  } else {
    console.error(error);
  }
};

export { bugsnagBootstrap, bugsnagInit, logError, setTelemetryUser };
